import React from 'react';
import { 
  Code2, 
  Rocket, 
  Globe, 
  Smartphone, 
  Layers, 
  Settings, 
  Users,
  Bot,
  Cog,
  Brain,
  Cloud,
  GitBranch,
  Wrench,
  TrendingUp,
  Search,
  Zap,
  BarChart3,
  Target,
  Mail
} from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const servicePillars = [
  {
    id: 'build',
    label: 'BUILD',
    title: 'Revenue-Generating Platforms',
    description: 'We engineer systems that create new revenue streams, reduce operational costs, and scale with your growth.',
    color: '#0A3D91',
    accentColor: 'rgba(10, 61, 145, 0.08)',
    services: [
      { icon: Rocket, name: 'MVP to Market', desc: 'Launch revenue-ready products in weeks, not months' },
      { icon: Globe, name: 'SaaS Platforms', desc: 'Multi-tenant systems built for recurring revenue' },
      { icon: Smartphone, name: 'Mobile & Web Apps', desc: 'High-conversion apps that drive engagement' },
      { icon: Layers, name: 'Enterprise Systems', desc: 'Custom platforms built for complex workflows' },
      { icon: Settings, name: 'Legacy Modernization', desc: 'Transform outdated systems into growth engines' },
      { icon: Users, name: 'Dedicated Teams', desc: 'Scale your engineering capacity on demand' },
    ],
  },
  {
    id: 'automate',
    label: 'AUTOMATE',
    title: 'Intelligent Operations',
    description: 'We deploy AI and automation that eliminate bottlenecks, reduce costs, and free your team for high-value work.',
    color: '#00BFFF',
    accentColor: 'rgba(0, 191, 255, 0.08)',
    services: [
      { icon: Bot, name: 'AI Agents', desc: 'Autonomous systems that handle complex workflows' },
      { icon: Cog, name: 'Process Automation', desc: 'Eliminate repetitive tasks and manual bottlenecks' },
      { icon: Brain, name: 'Predictive Intelligence', desc: 'AI that forecasts demand and optimizes decisions' },
      { icon: Cloud, name: 'Cloud Infrastructure', desc: 'Auto-scaling systems with high-availability SLAs' },
      { icon: GitBranch, name: 'Workflow Optimization', desc: 'End-to-end automation that minimizes errors' },
      { icon: Wrench, name: 'Managed Operations', desc: '24/7 monitoring with SLA-backed support' },
    ],
  },
  {
    id: 'grow',
    label: 'GROW',
    title: 'Scalable Growth Engines',
    description: 'We build acquisition systems designed to scale customer growth while reducing CAC and improving unit economics.',
    color: '#10B981',
    accentColor: 'rgba(16, 185, 129, 0.08)',
    services: [
      { icon: Search, name: 'Acquisition Strategy', desc: 'SEO, paid media, and organic channels that convert' },
      { icon: Zap, name: 'Growth Engineering', desc: 'Rapid experimentation to accelerate customer growth' },
      { icon: Target, name: 'Conversion Optimization', desc: 'Funnels engineered to maximize conversion rates' },
      { icon: BarChart3, name: 'Revenue Analytics', desc: 'Dashboards that surface actionable insights' },
      { icon: Mail, name: 'Lifecycle Automation', desc: 'Nurture sequences designed to increase LTV' },
    ],
  },
];

const ServicePillar = ({ pillar, index }) => {
  return (
    <div 
      className="relative"
      data-testid={`service-pillar-${pillar.id}`}
    >
      {/* Pillar Header */}
      <div className="mb-5">
        {/* Pillar Label */}
        <div className="flex items-center gap-3 mb-3">
          <span
            className="text-xs font-bold tracking-[0.25em] px-3 py-1 rounded-full"
            style={{ 
              color: pillar.color,
              backgroundColor: pillar.accentColor,
            }}
            data-testid={`pillar-label-${pillar.id}`}
          >
            {pillar.label}
          </span>
          <div 
            className="flex-1 h-px"
            style={{ backgroundColor: '#E2E8F0' }}
          />
        </div>

        {/* Pillar Title */}
        <h3
          className="text-xl sm:text-2xl font-bold tracking-tight mb-2"
          style={{ color: '#131B2E' }}
        >
          {pillar.title}
        </h3>

        {/* Pillar Description */}
        <p
          className="text-sm leading-relaxed"
          style={{ color: '#475569' }}
        >
          {pillar.description}
        </p>
      </div>

      {/* Services List */}
      <div className="space-y-2">
        {pillar.services.map((service, serviceIndex) => (
          <div
            key={serviceIndex}
            className="group flex items-start gap-3 p-3 rounded-lg transition-all duration-200 hover:bg-gray-50/50"
            data-testid={`service-item-${pillar.id}-${serviceIndex}`}
          >
            {/* Icon */}
            <div
              className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-200"
              style={{ 
                backgroundColor: pillar.accentColor,
              }}
            >
              <service.icon
                className="w-4 h-4 transition-colors duration-200"
                style={{ color: pillar.color }}
              />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h4
                className="text-sm font-semibold mb-0.5 transition-colors duration-200"
                style={{ color: '#131B2E' }}
              >
                {service.name}
              </h4>
              <p
                className="text-xs leading-relaxed"
                style={{ color: '#475569' }}
              >
                {service.desc}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ServicesSection = ({ onOpenContactModal }) => {
  const sectionRef = useScrollReveal();

  return (
    <section
      id="services"
      ref={sectionRef}
      data-testid="services-section"
      className="relative"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className="kv-container">
        <div className="pt-10 pb-12 lg:pt-12 lg:pb-14">
          {/* Section Header */}
          <div className="kv-reveal max-w-3xl mb-6">
            {/* Section Label */}
            <p
              className="text-xs font-semibold uppercase tracking-[0.2em] mb-3"
              style={{ color: '#0A3D91' }}
              data-testid="services-label"
            >
              What We Do
            </p>

            {/* Section Headline */}
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
              style={{ color: '#131B2E', letterSpacing: '-0.025em' }}
              data-testid="services-headline"
            >
              Service Pillars
            </h2>

            {/* Section Description */}
            <p
              className="text-lg"
              style={{ color: '#475569', lineHeight: '1.7' }}
            >
              Three integrated capabilities that drive{' '}
              <span className="font-semibold" style={{ color: '#0A3D91' }}>revenue growth</span>,{' '}
              <span className="font-semibold" style={{ color: '#00BFFF' }}>operational efficiency</span>, and{' '}
              <span className="font-semibold" style={{ color: '#10B981' }}>scalable acquisition</span>{' '}
              — delivering measurable ROI for high-growth companies.
            </p>
          </div>

          {/* Three Pillars Grid */}
          <div 
            className="kv-reveal-stagger grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6"
            data-testid="services-pillars-grid"
          >
            {servicePillars.map((pillar, index) => (
              <div key={pillar.id} className="kv-reveal">
                <ServicePillar pillar={pillar} index={index} />
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div 
            className="kv-reveal mt-6 pt-4 border-t text-center"
            style={{ borderColor: '#E2E8F0' }}
            data-testid="services-cta"
          >
            <p
              className="text-lg mb-2"
              style={{ color: '#131B2E' }}
            >
              Ready to see what's possible?
            </p>
            <p
              className="text-base mb-6"
              style={{ color: '#475569' }}
            >
              Let's discuss how we can drive measurable growth for your business.
            </p>
            <button
              onClick={onOpenContactModal}
              className="kv-btn-primary px-7 py-3 text-base"
              data-testid="services-contact-cta"
            >
              Book a Strategy Call
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
